import type { AnswerMetadata, AnswerSource } from '@api-client/api'
import { useWithAmpli } from '@contexts/AmpliContextProvider'
import { useCallback, useMemo } from 'react'
import { type FieldPath, useFormContext, useWatch } from 'react-hook-form'

// TODO(larryhudson): Make this hook not dependent on CorporateYearFormData / views folder
import type { CorporateYearFormData } from '../views/CorporateData/Edit/types'

interface usePrefillMetadataProps {
	fieldName: string
	metadataMapPath: FieldPath<CorporateYearFormData>
}

export interface PrefillableFieldProps {
	isPrefilled: boolean
	sources: readonly AnswerSource[]
	onFieldChange?: <T>(value: T, onChange: (value: T) => void) => void
}

const EMPTY_ARRAY = [] as const

export const EMPTY_PREFILL_PROPS = {
	isPrefilled: false,
	sources: EMPTY_ARRAY,
	onFieldChange: undefined,
} as const satisfies PrefillableFieldProps

export const usePrefillAttachments = () => {
	const { control } = useFormContext<CorporateYearFormData>()

	const reportAttachments = useWatch({
		control,
		name: 'sustainabilityPerformance.reportAttachments',
	})

	const additionalDocumentAttachments = useWatch({
		control,
		name: 'sustainabilityPerformance.additionalDocumentAttachments',
	})

	return useMemo(
		() =>
			// Concatenate both attachment lists
			[...(reportAttachments ?? []), ...(additionalDocumentAttachments ?? [])],
		[reportAttachments, additionalDocumentAttachments]
	)
}

/**
 * Hook to check if a field has AI-generated metadata and retrieve it
 * @param fieldName - The name of the field to check (e.g., 'hasComputedGhgEmissions')
 * @param metadataMapPath - Path to the metadata map in form data (e.g., 'targets.aiMetadataMap')
 * @returns Object with hasMetadata flag, metadata, attachments, and onFieldChange callback
 */
export const usePrefillableFieldProps = ({
	fieldName,
	metadataMapPath,
}: usePrefillMetadataProps): PrefillableFieldProps => {
	const { control, setValue } = useFormContext<CorporateYearFormData>()
	const withAmpli = useWithAmpli()

	// Watch the metadata map from the path
	const currentMetadataMap = useWatch({
		control,
		name: metadataMapPath,
	}) as Record<string, AnswerMetadata> | undefined

	// Create onFieldChange callback that clears metadata when field changes
	const onFieldChange = useCallback(
		<T>(value: T, onChange: (value: T) => void): void => {
			// Clear the field from the metadata map
			if (currentMetadataMap) {
				const wasPrefilled = currentMetadataMap[fieldName] !== undefined
				const newMap = Object.fromEntries(
					Object.entries(currentMetadataMap).filter(
						([key]) => key !== fieldName
					)
				)
				if (wasPrefilled) {
					withAmpli(ampli =>
						ampli.clickEvent({
							buttonText:
								'Corporate Data - Override field previously prefilled with AI',
							clickPageUrl: window.location.href,
						})
					)
				}
				setValue(metadataMapPath, newMap, {
					shouldDirty: true,
				})
			}
			// Call the original onChange
			onChange(value)
		},
		[metadataMapPath, currentMetadataMap, fieldName, setValue, withAmpli]
	)

	return useMemo(() => {
		const metadata = currentMetadataMap?.[fieldName]
		const isPrefilled = !!metadata && (metadata.sources?.length ?? 0) > 0

		return {
			isPrefilled,
			sources: metadata?.sources ?? EMPTY_ARRAY,
			onFieldChange,
		}
	}, [fieldName, currentMetadataMap, onFieldChange])
}
